import React, { useCallback, useEffect, useState } from 'react';

import { useFilter, useFleet, useHasPermission } from '../../hooks';
import { KMLEntry, MapboxMap, ParsedRouteDefinition, Route } from '../../interfaces';
import { RouteService } from '../../services';
import { hash } from '../../utils';
import { MapPopup } from '../atoms';
import { queryClient } from '../queryClient';
import { extractGoogleCoords } from '../reviewGridMatchedEvents/KMLParser';
import { KMLRoutesLayer } from './KMLRoutesLayer';

interface Props {
  map: MapboxMap;
  routeId?: number;
  showAllEventTypes?: boolean;
  onClick?: (e: React.MouseEventHandler<HTMLElement>) => void;
  routeDefinition?: ParsedRouteDefinition[];
}

export function RoutesLayer(props: Props) {
  const { map, routeId, showAllEventTypes, onClick } = props;

  const fleet = useFleet();
  const filter = useFilter();
  const route_ids = (filter.route_ids ?? []).map((id) => parseInt(id, 10));

  const [kmlRoutes, setKMLRoutes] = useState<ParsedRouteDefinition[]>(props.routeDefinition ?? []);
  const canListRoutes = useHasPermission('routes:read:list');
  const canViewRoute = useHasPermission('routes:read:resource');

  const [selectedPolygon, setSelectedPolygon] = useState<KMLEntry | null>(null);
  const [lat, setLat] = useState<number>(0);
  const [lon, setLon] = useState<number>(0);

  const handleClick = useCallback(
    (e: any) => {
      const feature = e.features[0];
      props.onClick?.(e);
      if (!feature) {
        setSelectedPolygon(null);
        return;
      }
      const data = JSON.parse(feature.properties.data);
      if (feature.geometry?.coordinates) {
        data.geometry = { coordinates: feature.geometry.coordinates };
      }
      setSelectedPolygon(data);
      setLat(e.lngLat.lat);
      setLon(e.lngLat.lng);
    },
    [onClick],
  );

  const turnOffPopup = (e) => {
    props.onClick?.(e);
    setSelectedPolygon(null);
  };

  useEffect(() => {
    if (fleet.uuid) {
      init();
    }
  }, [fleet.uuid, props.routeId, hash(filter.route_ids)]);

  useEffect(() => {
    setKMLRoutes(props.routeDefinition ?? []);
  }, [props.routeDefinition]);

  useEffect(() => {
    if (map) {
      map.on('click', turnOffPopup);
      return () => {
        map.off('click', turnOffPopup);
      };
    }
  }, [map]);

  const fetchRoutes = async (): Promise<Route[]> => {
    if (routeId) {
      if (!canViewRoute) {
        return [];
      }

      const route = await queryClient.ensureQueryData({
        queryKey: ['routes', 'route', routeId, fleet.id],
        queryFn: () => RouteService.get(routeId, true),
      });
      return [route];
    }

    if (!canListRoutes) {
      return [];
    }

    const allRoutes = await queryClient.ensureQueryData({
      queryKey: ['routes', fleet.id],
      queryFn: () => RouteService.getAll(),
    });

    if (route_ids.length > 0) {
      return allRoutes.filter((r) => route_ids.includes(r.id));
    }
    return allRoutes;
  };

  const fetchDefinitions = async (routes: Route[]) => {
    const routeIds = routes.map((route) => route.id.toString());

    if (!canViewRoute) {
      return [];
    }

    const requests = routeIds.map((route_id) => {
      return queryClient.ensureQueryData({
        queryKey: ['routes', 'definitions', route_id, fleet.id],
        queryFn: () => RouteService.loadRawRouteDefinition(route_id),
      });
    });
    return Promise.all(requests);
  };

  const init = async () => {
    if (props.routeDefinition) {
      return;
    }

    const routes = await fetchRoutes();
    const visibleRoutes = routes.filter((r) => !r.hidden);

    const definitions = (await fetchDefinitions(visibleRoutes)).filter(Boolean);

    const kmlRouteIds: Route['id'][] = [];

    const kmls: ParsedRouteDefinition[] = [];

    definitions.forEach((d, idx) => {
      const isKML = d.startsWith('<?xml');
      const routeId = visibleRoutes[idx].id;
      if (isKML) {
        kmlRouteIds.push(routeId);
        kmls.push(extractGoogleCoords(d));
      }
    });

    setKMLRoutes(kmls);
  };

  return (
    <>
      <MapPopup lat={lat} lon={lon} selectedMarker={selectedPolygon} />
      <KMLRoutesLayer
        map={map}
        routeDefinitions={kmlRoutes}
        showAllEventTypes={showAllEventTypes}
        onClick={handleClick}
      />
    </>
  );
}
